import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled, { css } from 'styled-components'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import LineCameraPlusIcon from 'LuxKit/Icons/line/LineCameraPlusIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import TripPlanBrowseRow from './TripPlanBrowseRow'
import productLabels from 'i18n/productLabels'
import type { BookedExperienceSummary, ExperienceTilePrice } from '../hooks/useTripPlanExperienceTileData'

const EXPERIENCE_TILE_ICON_BACKGROUND = 'rgba(30, 140, 140, 0.2)'
const EXPERIENCE_TILE_ICON_COLOUR = '#1E8C8C'

const ExperienceThumbnail = styled(ListItemThumbnail)<{ $placeholder?: boolean }>`
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: ${rem(10)};

  ${props => props.$placeholder && css`
    &&& {
      background-color: ${EXPERIENCE_TILE_ICON_BACKGROUND};
      color: ${EXPERIENCE_TILE_ICON_COLOUR};
    }
  `}

  svg {
    width: ${rem(16)};
    height: ${rem(16)};
  }
`

const ExperienceTileActions = styled(Group)`
  flex-shrink: 0;
`

interface Props {
  bookedExperienceSummary?: BookedExperienceSummary
  bookedExperienceCount: number
  experienceTilePrice?: ExperienceTilePrice
  hasSavedExperiences: boolean
  placeName: string
  onOpen: () => void
}

function TripPlanExperienceTile({
  bookedExperienceSummary,
  bookedExperienceCount,
  experienceTilePrice,
  hasSavedExperiences,
  placeName,
  onOpen,
}: Props) {
  const intl = useIntl()

  const experiencesLabel = intl.formatMessage(productLabels.experiences)

  const experienceTilePriceLabel = experienceTilePrice === undefined ? undefined : intl.formatMessage(
    {
      id: 'UwBcCE8twltyMVdADlka',
      defaultMessage: 'From {price}/person',
      description: 'Per-person experience price copy shown on the trip planner stop experience tile',
    },
    { price: <FormatCurrency value={experienceTilePrice.price} currency={experienceTilePrice.currency} format="roundedDollar" /> },
  )

  const bookedExperiencesLabel = intl.formatMessage(
    {
      id: '9cILNi4ZbWjHLm9-a0P1',
      defaultMessage: '{count, plural, one {# booked} other {# booked}}',
      description: 'Status badge showing the number of booked experiences in a trip planner stop',
    },
    { count: bookedExperienceCount },
  )

  // Nothing chosen for this stop yet, so the row invites a browse rather than summarising one.
  if (!bookedExperienceSummary && !hasSavedExperiences) {
    return (
      <TripPlanBrowseRow
        size="medium"
        title={intl.formatMessage(
          {
            id: 'LPxTqll2yWVhu8rudKGZ',
            defaultMessage: 'Explore top things to do in {placeName}',
            description: 'Call to action on the trip plan itinerary row that opens the things to do drawer for that stop',
          },
          { placeName },
        )}
        titleWeight="bold"
        onClick={onOpen}
        startIcon={
          <ExperienceThumbnail $placeholder kind="highlight-primary-dark" gutter="none">
            <LineCameraPlusIcon size="XS" />
          </ExperienceThumbnail>
        }
        endIcon={<LineAngleRightIcon size="S" />}
      />
    )
  }

  return (
    <TripPlanBrowseRow
      onClick={onOpen}
      size="medium"
      titleWeight="bold"
      title={bookedExperienceSummary?.name ?? experiencesLabel}
      titleLineClamp={1}
      subtitle={bookedExperienceSummary ? undefined : experienceTilePriceLabel}
      subtitleLineClamp={1}
      startIcon={
        <ExperienceThumbnail
          $placeholder={!bookedExperienceSummary}
          kind={bookedExperienceSummary ? 'neutral' : 'highlight-primary-dark'}
          gutter="none"
        >
          <LineTicketIcon size="XS" />
        </ExperienceThumbnail>
      }
    >
      <ExperienceTileActions direction="horizontal" gap={8} verticalAlign="center">
        {bookedExperienceSummary && (
          <StatusLabel variant="confirmation">
            {bookedExperiencesLabel}
          </StatusLabel>
        )}
        <LineAngleRightIcon size="S" />
      </ExperienceTileActions>
    </TripPlanBrowseRow>
  )
}

export default TripPlanExperienceTile
